import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Concession } from '../../lib/api';

interface DeviationDetailsProps {
  concession: Concession;
}

export function DeviationDetails({ concession }: DeviationDetailsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Deviation Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground">Part Number</Label>
            <p className="font-mono font-medium">{concession.part_number}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Quantity Affected</Label>
            <p className="font-medium">{concession.quantity_affected} {concession.unit_of_measure}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Deviation Type</Label>
            <p className="font-medium capitalize">{concession.deviation_type}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Batch Number</Label>
            <p className="font-medium">{concession.batch_number || 'N/A'}</p>
          </div>
          {/* CR-6510: the legacy form's identifiers. Rendered only when the
              supplier filled them in — an empty row tells the reviewer
              nothing and pushes the substance further down the page. */}
          {concession.supplier_ref && (
            <div>
              <Label className="text-muted-foreground">Supplier Ref.</Label>
              <p className="font-medium">{concession.supplier_ref}</p>
            </div>
          )}
          {concession.mm_and_rev && (
            <div>
              <Label className="text-muted-foreground">MM &amp; Rev</Label>
              <p className="font-medium">{concession.mm_and_rev}</p>
            </div>
          )}
          {concession.previous_deviation_ref && (
            <div>
              <Label className="text-muted-foreground">Previous Deviation Ref.</Label>
              <p className="font-medium">{concession.previous_deviation_ref}</p>
            </div>
          )}
          {concession.serial_numbers && (
            <div>
              <Label className="text-muted-foreground">Serial No(s)</Label>
              <p className="font-medium">{concession.serial_numbers}</p>
            </div>
          )}
        </div>

        {/* CR-6510: what was specified, before what actually happened. */}
        {concession.customer_requirement && (
          <div>
            <Label className="text-muted-foreground">Customer Requirement</Label>
            <p className="mt-1 whitespace-pre-wrap">{concession.customer_requirement}</p>
          </div>
        )}

        <div>
          <Label className="text-muted-foreground">Description</Label>
          <p className="mt-1 whitespace-pre-wrap">{concession.description}</p>
        </div>

        <div>
          <Label className="text-muted-foreground">Reason for Request</Label>
          <p className="mt-1 whitespace-pre-wrap">{concession.reason}</p>
        </div>

        {concession.proposed_disposition && (
          <div>
            <Label className="text-muted-foreground">Proposed Disposition</Label>
            <p className="mt-1 whitespace-pre-wrap">{concession.proposed_disposition}</p>
          </div>
        )}

        {concession.risk_justification && (
          <div>
            <Label className="text-muted-foreground">Risk Justification</Label>
            <p className="mt-1 whitespace-pre-wrap">{concession.risk_justification}</p>
          </div>
        )}

        {/* CR-6510: the ticket calls this out as required for quality
            sign-off, so it sits last — the thing a reviewer reads before
            deciding. */}
        {concession.root_cause_corrective_action && (
          <div>
            <Label className="text-muted-foreground">Root Cause &amp; Corrective Action</Label>
            <p className="mt-1 whitespace-pre-wrap">{concession.root_cause_corrective_action}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
